<template>
  <div class="my-space-page">

    <!-- ARRIBA: buscador (50%) + accesos directos apilados en la columna
         izquierda; a la derecha, Recordatorios y Calendario ocupan ambas
         filas (ver .my-space-top-grid, grid-row: span 2). -->
    <div class="my-space-top-grid">
      <div class="my-space-hero">
        <CustomerBrandLogo
          v-if="hasCustomerBrand"
          v-show="!isMobileViewport"
          class="my-space-hero-company my-space-hero-company--full"
          variant="full"
          context="my-space"
          :alt="projectName"
        />
        <CustomerBrandLogo
          v-if="hasCustomerBrand"
          v-show="isMobileViewport"
          class="my-space-hero-company my-space-hero-company--compact"
          variant="compact"
          context="my-space"
          :alt="projectName"
        />
        <div class="my-space-hero-content">
          <div class="my-space-hero-label">Mi espacio</div>
          <div class="my-space-hero-greeting">{{ greeting }}, {{ userName }}</div>
          <div class="my-space-hero-date">{{ todayLabel }}</div>

          <div class="my-space-hero-summary"><MySpaceRemindersSummaryWidget v-if="access.reminders" /><MySpaceTasksSummaryWidget v-if="access.tasks" /></div>
        </div>

        <!-- BUSCADOR PROTAGONISTA -->
        <div class="my-space-search" @click="openPalette">
          <AppIcon name="search" class="my-space-search-icon"></AppIcon>
          <span class="my-space-search-placeholder">¿Qué necesitás hacer?</span>
          <span class="my-space-search-kbd"><kbd>Ctrl</kbd><kbd>K</kbd></span>
        </div>
      </div>

      <!-- RECORDATORIOS -->
      <MySpaceRemindersWidget v-if="access.reminders" />

      <!-- AGENDA EXTERNA: cualquier proveedor que entregue una URL iCalendar -->
      <div class="my-space-card my-space-card--calendar" :class="{ 'my-space-card--mobile-collapsed': collapsedWidgets.calendar }">
        <div class="my-space-card-header">
          <span class="my-space-card-title"><span class="my-space-card-icon"><AppIcon name="calendar_month"></AppIcon></span> Mi agenda</span>
          <span class="my-space-calendar-header-actions">
            <button type="button"
                    class="my-space-card-refresh"
                    :disabled="loading.calendar || refreshing.calendar || !calendar.configuration.configured"
                    v-b-tooltip.hover
                    title="Actualizar agenda"
                    aria-label="Actualizar agenda"
                    @click.stop="refreshCalendar">
              <AppIcon name="refresh" :class="{ 'my-space-card-refresh__icon--spinning': refreshing.calendar }"></AppIcon>
            </button>
            <button type="button" class="my-space-calendar-configure" @click="openCalendarConfiguration">
              <AppIcon name="settings"></AppIcon> Configurar
            </button>
            <button type="button" class="my-space-card-collapse" :aria-expanded="String(!collapsedWidgets.calendar)" @click="toggleWidgetCollapse('calendar')">
              <AppIcon :name="collapsedWidgets.calendar ? 'expand_more' : 'expand_less'"></AppIcon>
            </button>
          </span>
        </div>
        <div class="my-space-card-body">
          <div class="my-space-card-loading" v-if="loading.calendar"><b-spinner small></b-spinner></div>
          <template v-else-if="calendar.configuration.configured">
            <div class="my-space-calendar-day-label">
              <span>HOY</span>
              {{ todayLabel }}
              <small v-if="calendar.configuration.name">· {{ calendar.configuration.name }}</small>
            </div>
            <div v-for="event in calendarEventsOrdered"
                 :key="event.id"
                 class="my-space-calendar-event"
                 :class="[
                   'my-space-calendar-event--' + event.status.key,
                   {
                     'my-space-calendar-event--linked': event.url,
                     'my-space-calendar-event--active': event.is_active,
                     'my-space-calendar-event--finished': isCalendarEventFinished(event),
                   },
                 ]"
                 :role="event.url ? 'button' : null"
                 :tabindex="event.url ? 0 : null"
                 @click="openCalendarEvent(event)"
                 @keyup.enter="openCalendarEvent(event)">
              <span class="my-space-calendar-event__time" :class="{ 'my-space-calendar-event__time--previous-day': event.started_previous_day }">
                <template v-if="event.started_previous_day"><small>Ayer</small><strong>{{ event.start }}</strong></template>
                <template v-else>{{ event.all_day ? 'Todo el día' : event.start }}</template>
              </span>
              <span class="my-space-calendar-event__line"></span>
              <span class="my-space-calendar-event__copy">
                <span class="my-space-calendar-event__heading">
                  <strong>{{ event.title }}</strong>
                  <b-badge v-if="event.is_active" class="my-space-calendar-event__active">En curso</b-badge>
                  <b-badge v-else-if="isCalendarEventFinished(event)" class="my-space-calendar-event__finished">Finalizado</b-badge>
                  <b-badge class="my-space-calendar-event__status" :class="'my-space-calendar-event__status--' + event.status.key">{{ event.status.label }}</b-badge>
                </span>
                <small v-if="event.description" class="my-space-calendar-event__description" :title="event.description">{{ event.description }}</small>
                <small v-else-if="event.end && !event.all_day">hasta {{ event.end }}</small>
                <small v-if="event.recurrence" class="my-space-calendar-event__recurrence"><AppIcon name="repeat"></AppIcon> {{ event.recurrence }}</small>
              </span>
              <span class="my-space-calendar-event__actions">
                <button v-if="event.location_url"
                        type="button"
                        class="my-space-calendar-event__action"
                        v-b-tooltip.hover
                        title="Abrir ubicación"
                        aria-label="Abrir ubicación"
                        @click.stop="openExternalCalendarLink(event.location_url)">
                  <AppIcon name="location_on"></AppIcon>
                </button>
                <button v-if="event.meeting_url"
                        type="button"
                        class="my-space-calendar-event__action"
                        v-b-tooltip.hover
                        title="Unirse a la videollamada"
                        aria-label="Unirse a la videollamada"
                        @click.stop="openExternalCalendarLink(event.meeting_url)">
                  <AppIcon name="videocam"></AppIcon>
                </button>
                <span v-if="event.url" class="my-space-calendar-event__link" v-b-tooltip.hover title="Abrir evento">
                  <AppIcon name="open_in_new"></AppIcon>
                </span>
              </span>
            </div>
            <EmptyState v-if="!calendar.events.length"
                        icon="event_available"
                        title="Tu día está despejado"
                        description="No tenés eventos para hoy." />
          </template>
          <div v-else class="my-space-calendar-welcome">
            <span class="my-space-calendar-welcome__icon"><AppIcon name="calendar_month"></AppIcon></span>
            <strong>Tu agenda, en un solo lugar</strong>
            <p>Conectá una URL privada iCalendar de Google, Outlook o cualquier otro calendario para ver los eventos de hoy.</p>
            <button type="button" class="my-space-calendar-primary-action" @click="openCalendarConfiguration">
              <AppIcon name="link"></AppIcon> Configurar agenda
            </button>
          </div>
        </div>
      </div>

      <!-- ACCESOS DIRECTOS -->
      <div class="my-space-card my-space-card--accesos">
        <div class="my-space-card-header">
          <span class="my-space-card-title"><span class="my-space-card-icon"><AppIcon name="push-pin" fill></AppIcon></span> Accesos directos</span>
        </div>
        <div class="my-space-card-body">
          <div class="my-space-tiles-frame">
            <button type="button"
                    v-if="tilesOverflow"
                    class="my-space-tiles-nav my-space-tiles-nav--prev"
                    :disabled="!canScrollTilesLeft"
                    v-b-tooltip.hover
                    title="Ver anteriores"
                    @click="scrollTiles(-1)">
              <AppIcon name="chevron-left"></AppIcon>
            </button>

            <div class="my-space-tiles" ref="tilesScroll" @scroll="updateTilesScrollState">
              <draggable tag="div"
                         class="my-space-tiles-drag"
                         :list="shortcutItems"
                         :disabled="isMobileViewport"
                         filter=".my-space-tile-remove"
                         :prevent-on-filter="false"
                         :animation="150"
                         ghost-class="my-space-tile--ghost"
                         drag-class="my-space-tile--dragging"
                         @end="onReorderShortcuts">
                <div v-for="item in shortcutItems"
                     :key="item.fullPath"
                     class="my-space-tile"
                     :class="'my-space-tile--' + tileTypeVariant(item)"
                     @click="navigate(item.fullPath)">
                  <button type="button"
                          class="my-space-tile-remove"
                          v-b-tooltip.hover
                          title="Quitar de Mi espacio"
                          @click.stop="removeShortcut(item)">
                    <AppIcon name="close"></AppIcon>
                  </button>
                  <span class="my-space-tile-icon"><AppIcon :name="tileIcon(item)" /></span>
                  <span class="my-space-tile-title">{{ item.title }}</span>
                  <span class="my-space-tile-type">{{ tileTypeLabel(item) }}</span>
                </div>
              </draggable>

              <div class="my-space-tile my-space-tile--add" @click="openShortcutPicker">
                <span class="my-space-tile-icon"><AppIcon name="plus"></AppIcon></span>
                <span class="my-space-tile-title">Agregar</span>
              </div>
            </div>

            <button type="button"
                    v-if="tilesOverflow"
                    class="my-space-tiles-nav my-space-tiles-nav--next"
                    :disabled="!canScrollTilesRight"
                    v-b-tooltip.hover
                    title="Ver siguientes"
                    @click="scrollTiles(1)">
              <AppIcon name="chevron-right"></AppIcon>
            </button>
          </div>

          <div class="my-space-tiles-hint text-muted" v-if="!shortcutItems.length">
            Tocá <strong>Agregar</strong> para sumar accesos directos a vistas o acciones.
          </div>
        </div>
      </div>
    </div>

    <div class="my-space-layout">
      <div class="my-space-main">

        <!-- TU DÍA: sin título de sección — cada card ya lleva el suyo
             propio en el header, repetirlo afuera quedaba redundante. -->
        <div v-if="access.tasks" class="my-space-day-grid">
          <MySpacePersonalTasksWidget />
          <MySpacePriorityTasksWidget />
          <MySpacePriorityTasksByAssigneeWidget />
        </div>
      </div>
      <!-- ACCESO A TABLEROS: columna lateral. -->
      <div class="my-space-side">
        <div class="my-space-card my-space-dashboard-shortcut">
          <div class="my-space-dashboard-shortcut__content">
            <div class="my-space-dashboard-shortcut__heading">
              <span class="my-space-dashboard-shortcut__icon"><AppIcon name="dashboard"></AppIcon></span>
              <span class="my-space-dashboard-shortcut__eyebrow">Acceso rápido</span>
            </div>
            <strong class="my-space-dashboard-shortcut__title">Tus tableros</strong>
            <span class="my-space-dashboard-shortcut__description">Elegí un tablero para consultar sus indicadores y métricas.</span>

            <div v-if="dashboardItems.length" class="my-space-dashboard-shortcut__list" aria-label="Tableros disponibles">
              <button v-for="item in dashboardItems"
                      :key="item.id"
                      type="button"
                      class="my-space-dashboard-shortcut__item"
                      @click="goDashboardItem(item)">
                <span class="my-space-dashboard-shortcut__item-icon"><AppIcon name="dashboard"></AppIcon></span>
                <span class="my-space-dashboard-shortcut__item-copy">
                  <strong>{{ item.name }}</strong>
                  <small>{{ item.widgetCount }} {{ item.widgetCount === 1 ? 'widget' : 'widgets' }}</small>
                </span>
                <AppIcon name="chevron_right" class="my-space-dashboard-shortcut__item-arrow"></AppIcon>
              </button>
            </div>
            <span v-else class="my-space-dashboard-shortcut__empty">Todavía no creaste tableros para este espacio.</span>
          </div>
          <button type="button" class="my-space-dashboard-shortcut__action" @click="goDashboard">
            Ver todos los tableros <AppIcon name="arrow_forward" class="my-space-dashboard-shortcut__arrow"></AppIcon>
          </button>
        </div>
      </div>
    </div>

    <b-modal v-model="calendarConfigOpen"
             title="Configurar mi agenda"
             centered
             hide-footer
             @hidden="calendarConfigError = ''">
      <p class="my-space-calendar-modal-intro">Pegá el enlace privado <strong>iCalendar (.ics)</strong> de Google Calendar, Outlook u otro proveedor. Zyncro solo lo consulta para mostrar tu agenda: no puede editar eventos externos.</p>
      <b-form-group label="Nombre para identificarlo">
        <b-form-input v-model.trim="calendarForm.name" placeholder="Ej.: Calendario personal"></b-form-input>
      </b-form-group>
      <b-form-group label="URL iCalendar (.ics)">
        <b-form-input v-model.trim="calendarForm.url" type="url" placeholder="https://.../calendar.ics"></b-form-input>
        <small class="form-text text-muted">Usá el enlace secreto/de suscripción del calendario. Debe ser una URL https pública.</small>
      </b-form-group>
      <b-alert v-if="calendarConfigError" show variant="danger" class="mb-3">{{ calendarConfigError }}</b-alert>
      <div class="d-flex justify-content-between align-items-center">
        <button v-if="calendar.configuration.configured" type="button" class="my-space-calendar-unlink" :disabled="savingCalendar" @click="requestClearCalendarConfiguration">
          <AppIcon name="link_off"></AppIcon> Desvincular calendario
        </button>
        <span v-else></span>
        <div>
          <button type="button" class="btn btn-light mr-2" :disabled="savingCalendar" @click="calendarConfigOpen = false">Cancelar</button>
          <button type="button" class="btn btn-dark" :disabled="savingCalendar" @click="saveCalendarConfiguration">
            <b-spinner v-if="savingCalendar" small></b-spinner><span v-else>Guardar agenda</span>
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import MySpaceRemindersWidget from '@/components/modules/users/reminders/widgets/MySpaceRemindersWidget'
import MySpaceRemindersSummaryWidget from '@/components/modules/users/reminders/widgets/MySpaceRemindersSummaryWidget'
import MySpacePersonalTasksWidget from '@/components/modules/users/tasks/widgets/MySpacePersonalTasksWidget'
import MySpacePriorityTasksWidget from '@/components/modules/users/tasks/widgets/MySpacePriorityTasksWidget'
import MySpacePriorityTasksByAssigneeWidget from '@/components/modules/users/tasks/widgets/MySpacePriorityTasksByAssigneeWidget'
import MySpaceTasksSummaryWidget from '@/components/modules/users/tasks/widgets/MySpaceTasksSummaryWidget'
import serviceCalendar from '@/components/inc/dashboard/calendar/services'
import Session from '@/handler/session'
import ErrorToken from '@/handler/errorToken'
import shortcuts from '@/handler/mySpaceShortcuts'
import Menu from '@/components/containers/menu'
import { EventBus } from '@/handler/event-bus'
import Branding from '@/components/branding/branding'
import CustomerBrandLogo from '@/components/branding/CustomerBrandLogo'
import moment from 'moment'
import draggable from 'vuedraggable'

export default {
  name: 'MySpace',
  components: { CustomerBrandLogo, MySpaceRemindersWidget, MySpaceRemindersSummaryWidget, MySpacePersonalTasksWidget, MySpacePriorityTasksWidget, MySpacePriorityTasksByAssigneeWidget, MySpaceTasksSummaryWidget, draggable },
  data: () => ({
    userName: '', access: { tasks: false, reminders: false },
    loading: { calendar: true }, refreshing: { calendar: false },
    calendar: { configuration: { name: null, url: null, configured: false }, events: [] },
    calendarConfigOpen: false, savingCalendar: false, calendarConfigError: '', calendarForm: { name: '', url: '' },
    shortcutItems: [], nav: [], tilesOverflow: false, canScrollTilesLeft: false, canScrollTilesRight: false, isMobileViewport: false,
    autoRefreshTimer: null, collapsedWidgets: { calendar: false }, brandingVersion: 0,
  }),
  created() { EventBus.$on('SpaceShortcuts:updated', this.loadShortcuts); EventBus.$on('Settings:settings', this.refreshBranding) },
  mounted() {
    this.setup(); this.isMobileViewport = window.innerWidth <= 767; if (this.isMobileViewport) this.collapsedWidgets.calendar = true; this.startAutoRefresh()
    window.addEventListener('resize', this.updateTilesScrollState); document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeDestroy() {
    EventBus.$off('SpaceShortcuts:updated', this.loadShortcuts); EventBus.$off('Settings:settings', this.refreshBranding)
    this.stopAutoRefresh(); window.removeEventListener('resize', this.updateTilesScrollState); document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
  computed: {
    greeting() { var hour = moment().hour(); return hour < 6 ? 'Buenas noches' : (hour < 12 ? 'Buen día' : (hour < 19 ? 'Buenas tardes' : 'Buenas noches')) },
    todayLabel() { var label = moment().format('dddd D [de] MMMM'); return label.charAt(0).toUpperCase() + label.slice(1) },
    projectName() { var session = Session.getSession(); return session && session.settings ? session.settings.proyect_name || 'Empresa' : 'Empresa' },
    hasCustomerBrand() { this.brandingVersion; var session = Session.getSession(); var branding = Branding.resolve(session && session.settings); return (branding.logoCustomized && branding.logo) || (branding.iconCustomized && branding.icon) },
    calendarEventsOrdered() {
      return this.calendar.events.slice().sort((a, b) => {
        // Los eventos que continúan desde ayer pertenecen a la fecha anterior,
        // aunque el backend los incluya en la agenda de hoy por seguir activos.
        var dateOrder = Number(!!b.started_previous_day) - Number(!!a.started_previous_day)
        return dateOrder || (a.start || '').localeCompare(b.start || '')
      })
    },
    dashboardItems() {
      var session = Session.getSession(); var dashboards = session && session.auth && session.auth.user && session.auth.user.dashboard
      return Array.isArray(dashboards) ? dashboards.filter(item => item && item.id && item.name).map(item => { var data = Array.isArray(item.data) ? item.data : []; try { if (!Array.isArray(item.data) && item.data) data = JSON.parse(item.data) } catch (e) { data = [] } return { id: item.id, name: item.name, widgetCount: data.length } }) : []
    },
  },
  watch: { shortcutItems() { this.$nextTick(this.updateTilesScrollState) } },
  methods: {
    setup() {
      var session = Session.getSession(); if (!session.auth) return
      this.userName = session.auth.user.selector_session_name || ''; var permissions = session.auth.user.permissions
      this.nav = permissions && permissions.menu ? Menu.getItems(permissions.menu) : []
      this.access.tasks = this.hasModuleAccess(session, 'tasks'); this.access.reminders = this.hasModuleAccess(session, 'reminders')
      this.loadShortcuts(); this.syncShortcutsFromServer(); this.loadCalendar()
    },
    hasModuleAccess(session, router) { var menu = session.auth.user.permissions && session.auth.user.permissions.menu; return !!(menu && Object.keys(menu).some(key => menu[key].router === router)) },
    startAutoRefresh() { this.stopAutoRefresh(); this.autoRefreshTimer = window.setInterval(this.refreshMySpaceWidgets, 5 * 60 * 1000) },
    stopAutoRefresh() { if (this.autoRefreshTimer) { window.clearInterval(this.autoRefreshTimer); this.autoRefreshTimer = null } },
    handleVisibilityChange() { if (!document.hidden) this.refreshMySpaceWidgets() },
    refreshMySpaceWidgets() { if (document.hidden || this.calendarConfigOpen) return; if (this.access.reminders) EventBus.$emit('MySpace:reminders-refresh'); if (this.access.tasks) EventBus.$emit('MySpace:tasks-refresh'); if (this.calendar.configuration.configured && !this.loading.calendar && !this.refreshing.calendar) this.loadCalendar(true, true) },
    loadCalendar(isRefresh = false, silent = false, forceRefresh = false) { if (isRefresh) this.refreshing.calendar = true; (forceRefresh ? serviceCalendar.actualizarMiCalendario() : serviceCalendar.obtenerMiCalendario()).then(response => { var data = response.data || {}; this.calendar.configuration = data.configuration || { name: null, url: null, configured: false }; this.calendar.events = Array.isArray(data.events) ? data.events : []; this.loading.calendar = false; this.refreshing.calendar = false }).catch(error => { this.loading.calendar = false; this.refreshing.calendar = false; if (!silent) this.$awn.alert(ErrorToken.valid(error)) }) },
    refreshCalendar() { if (!this.loading.calendar && !this.refreshing.calendar && this.calendar.configuration.configured) this.loadCalendar(true, false, true) },
    isCalendarEventFinished(event) { if (!event || event.all_day || (event.status && event.status.key === 'cancelled')) return false; if (event.is_finished) return true; var now = moment(); var end = moment(now.format('YYYY-MM-DD') + ' ' + (event.end || event.start), 'YYYY-MM-DD HH:mm', true); return end.isValid() && now.isAfter(end) },
    openCalendarConfiguration() { this.calendarForm = { name: this.calendar.configuration.name || '', url: this.calendar.configuration.url || '' }; this.calendarConfigError = ''; this.calendarConfigOpen = true },
    saveCalendarConfiguration() { if (!this.calendarForm.url) { this.calendarConfigError = 'Ingresá la URL iCalendar del calendario.'; return } this.savingCalendar = true; serviceCalendar.guardarMiCalendario({ calendar_ical_name: this.calendarForm.name, calendar_ical_url: this.calendarForm.url }).then(() => { this.savingCalendar = false; this.calendarConfigOpen = false; this.$awn.success('Agenda guardada'); this.loadCalendar(true, false, true) }).catch(error => { this.savingCalendar = false; this.calendarConfigError = ErrorToken.valid(error) }) },
    requestClearCalendarConfiguration() { this.$bvModal.msgBoxConfirm('Vas a dejar de ver los eventos de este calendario en Mi espacio.', { title: '¿Desvincular calendario?', okTitle: 'DESVINCULAR', cancelTitle: 'Cancelar', centered: true }).then(confirmed => { if (confirmed) this.clearCalendarConfiguration() }) },
    clearCalendarConfiguration() { this.savingCalendar = true; serviceCalendar.guardarMiCalendario({ calendar_ical_name: '', calendar_ical_url: '' }).then(() => { this.savingCalendar = false; this.calendarConfigOpen = false; this.calendar.configuration = { name: null, url: null, configured: false }; this.calendar.events = []; this.$awn.success('Agenda desconectada') }).catch(error => { this.savingCalendar = false; this.calendarConfigError = ErrorToken.valid(error) }) },
    openCalendarEvent(event) { if (event && event.url) this.openExternalCalendarLink(event.url) },
    openExternalCalendarLink(url) { if (url) window.open(url, '_blank', 'noopener') },
    loadShortcuts() { this.shortcutItems = shortcuts.all().filter(item => item.type !== 'divider') },
    syncShortcutsFromServer() { shortcuts.syncFromServer().then(list => { this.shortcutItems = (list || []).filter(item => item.type !== 'divider') }) },
    scrollTiles(direction) { var el = this.$refs.tilesScroll; if (el) el.scrollBy({ left: direction * el.clientWidth * .8, behavior: 'smooth' }) },
    updateTilesScrollState() { this.isMobileViewport = window.innerWidth <= 767; var el = this.$refs.tilesScroll; if (!el) return; this.tilesOverflow = el.scrollWidth > el.clientWidth + 1; this.canScrollTilesLeft = el.scrollLeft > 4; this.canScrollTilesRight = el.scrollLeft < el.scrollWidth - el.clientWidth - 4 },
    tileIcon(item) { return this.findNavIconByPath(this.nav, item.fullPath) || 'fa fa-bookmark' },
    tileTypeVariant(item) { return item.type === 'action' ? 'action' : (item.type === 'dashboard' ? 'dashboard' : 'view') },
    tileTypeLabel(item) { return item.type === 'action' ? 'Acción' : (item.type === 'dashboard' ? 'Tablero' : 'Vista') },
    findNavIconByPath(nodes, path) { var found = null; (nodes || []).forEach(node => { if (node.url && (node.url === path || path.indexOf(node.url) === 0)) found = node.icon || found; if (node.children) { var icon = this.findNavIconByPath(node.children, path); if (icon) found = icon } }); return found },
    openPalette() { EventBus.$emit('CommandPalette:open') }, openShortcutPicker() { EventBus.$emit('CommandPalette:open', 'space') },
    removeShortcut(item) { shortcuts.remove(item.fullPath); this.loadShortcuts(); EventBus.$emit('SpaceShortcuts:updated', this.shortcutItems) },
    onReorderShortcuts() { shortcuts.reorder(this.shortcutItems); EventBus.$emit('SpaceShortcuts:updated', this.shortcutItems) },
    navigate(path) { if (path && path !== this.$route.fullPath) this.$router.push(path).catch(() => {}) },
    goDashboard() { this.$router.push({ name: 'DashboardStaff' }).catch(() => {}) }, goDashboardItem(item) { this.$router.push({ name: 'DashboardStaff', query: { dashboard: String(item.id) } }).catch(() => {}) },
  },
}
</script>

<style>
/* min-height + flex: la última fila ("Tu día") crece para ocupar el resto
   del viewport en vez de dejar espacio en blanco debajo. 123px = header
   fijo (55) + separación superior (6) + footer de la plataforma (56) + aire
   inferior (6), medido en vivo — no depende de que los contenedores padre
   tengan su propia cadena de height:100%. Si el header o el footer cambian
   de alto, este número hay que volver a medirlo. */
.my-space-page {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 134px);
  max-width: 100%;
}
/* "Tu día" (los 3 bloques de tareas) al 75%; el acceso a Tableros ocupa el 25%
   restante como sidebar, estirado por el "align-items: stretch" default
   del grid (ver .my-space-side). flex:1 en .my-space-page la empuja a
   ocupar el resto de la altura disponible — así los bloques vacíos no
   quedan como una caja chica con espacio en blanco debajo. */
.my-space-layout {
  display: grid;
  grid-template-columns: 3fr 1fr;
  grid-template-rows: 1fr;
  gap: 26px;
  align-items: stretch;
  flex: 1;
}
.my-space-main {
  min-width: 0;
}
.my-space-side {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.my-space-side .my-space-card {
  flex: 1;
}
.my-space-card.my-space-dashboard-shortcut {
  position: relative;
  flex: 0 1 340px;
  height: 340px;
  min-height: 0;
  padding: 18px;
  overflow: hidden;
  border: 0;
  background: linear-gradient(145deg, var(--app-color-primary) 0%, var(--app-color-primary-hover) 58%, var(--app-color-primary-active) 100%);
  color: var(--app-color-on-primary);
  box-shadow: var(--app-shadow-lg);
}
.my-space-dashboard-shortcut__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  min-height: 0;
}
.my-space-dashboard-shortcut__heading {
  display: flex;
  align-items: center;
  gap: 9px;
}
.my-space-dashboard-shortcut__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: var(--app-color-surface-translucent);
  color: var(--app-color-primary-hover);
  font-size: 18px;
}
.my-space-dashboard-shortcut__eyebrow {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .09em;
  opacity: .78;
  text-transform: uppercase;
}
.my-space-dashboard-shortcut__title {
  margin-top: 7px;
  font-size: var(--app-text-xl);
  line-height: 1.2;
}
.my-space-dashboard-shortcut__description {
  margin-top: 5px;
  font-size: var(--app-text-sm);
  line-height: 1.45;
  opacity: .86;
}
.my-space-dashboard-shortcut__list {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 7px;
  width: 100%;
  margin-top: 10px;
  overflow-y: auto;
  padding-right: 2px;
}
.my-space-dashboard-shortcut__item {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  min-height: 40px;
  padding: 5px 7px;
  border: 1px solid rgba(255, 255, 255, .2);
  border-radius: 9px;
  background: rgba(255, 255, 255, .09);
  color: var(--app-color-on-primary);
  cursor: pointer;
  text-align: left;
  transition: background-color .16s ease, border-color .16s ease, transform .16s ease;
}
.my-space-dashboard-shortcut__item:hover,
.my-space-dashboard-shortcut__item:focus {
  border-color: rgba(255, 255, 255, .48);
  background: rgba(255, 255, 255, .18);
  color: var(--app-color-on-primary);
  outline: none;
  transform: translateX(2px);
}
.my-space-dashboard-shortcut__item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 25px;
  width: 25px;
  height: 25px;
  border-radius: 7px;
  background: rgba(255, 255, 255, .18);
  font-size: 15px;
}
.my-space-dashboard-shortcut__item-copy {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  flex-direction: column;
  gap: 1px;
}
.my-space-dashboard-shortcut__item-copy strong {
  overflow: hidden;
  font-size: var(--app-text-sm);
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.my-space-dashboard-shortcut__item-copy small {
  overflow: hidden;
  font-size: 10px;
  line-height: 1.2;
  opacity: .72;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.my-space-dashboard-shortcut__item-arrow {
  flex: 0 0 auto;
  font-size: 18px;
  opacity: .72;
}
.my-space-dashboard-shortcut__empty {
  margin-top: 15px;
  font-size: var(--app-text-sm);
  line-height: 1.45;
  opacity: .82;
}
.my-space-dashboard-shortcut__action {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 13px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--app-color-on-primary);
  font-size: var(--app-text-sm);
  font-weight: var(--app-font-bold);
  cursor: pointer;
  text-align: left;
}
.my-space-dashboard-shortcut__action:hover,
.my-space-dashboard-shortcut__action:focus {
  color: var(--app-color-on-primary);
  outline: none;
  text-decoration: underline;
}
.my-space-dashboard-shortcut__arrow {
  transition: transform .18s ease;
}
.my-space-dashboard-shortcut:hover .my-space-dashboard-shortcut__arrow,
.my-space-dashboard-shortcut:focus .my-space-dashboard-shortcut__arrow {
  transform: translateX(4px);
}

/* Buscador + Accesos directos apilados en la columna izquierda (50%, dos
   filas); Recordatorios y Calendario ocupan las columnas restantes (25%
   cada una) pero abarcan ambas filas ("grid-row: span 2") para llegar desde
   la altura del buscador hasta el final de Accesos directos. */
.my-space-top-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  grid-template-rows: auto auto;
  gap: 26px;
  margin-bottom: 26px;
}
.my-space-top-grid > .my-space-hero {
  grid-column: 1;
  grid-row: 1;
}
.my-space-card--accesos {
  grid-column: 1;
  grid-row: 2;
}
.my-space-card--reminders-top {
  grid-column: 2;
  grid-row: 1 / span 2;
}
.my-space-card--calendar {
  grid-column: 3;
  grid-row: 1 / span 2;
}

/* PRESENTACIÓN */
.my-space-hero {
  position: relative;
  border-radius: 16px;
  padding: 28px 28px 34px;
  background: linear-gradient(130deg, var(--app-color-primary) 0%, var(--app-color-primary-hover) 55%, var(--app-color-primary-active) 100%);
  color: var(--app-color-on-primary);
  box-shadow: var(--app-shadow-lg);
  overflow: hidden;
}
/* Textura decorativa sobre el gradiente primario: no existe token para un
   blanco translúcido parcial, se conserva como color excepcional
   justificado (docs/design-system/color-palette/MIGRATION_CHECKLIST.md). */
.my-space-hero::before {
  content: "";
  position: absolute;
  top: -60px;
  right: -60px;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.07);
}
.my-space-hero::after {
  content: "";
  position: absolute;
  bottom: -80px;
  right: 90px;
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
}
.my-space-hero-content {
  position: relative;
  z-index: 1;
}
.my-space-hero-company {
  position: absolute;
  top: 18px;
  right: 20px;
  z-index: 2;
}
.my-space-hero-company--compact {
  display: none;
}
.my-space-hero-label {
  margin-bottom: 4px;
  font-size: var(--app-text-xs);
  font-weight: var(--app-font-bold);
  letter-spacing: 0.8px;
  opacity: 0.75;
  text-transform: uppercase;
}
.my-space-hero-greeting {
  font-size: var(--app-text-xl);
  font-weight: var(--app-font-bold);
  letter-spacing: 0.2px;
}
.my-space-hero-date {
  font-size: var(--app-text-sm);
  opacity: 0.85;
  margin-top: 2px;
}
.my-space-hero-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}
.my-space-hero-summary-widget { display: contents; }
.my-space-hero-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.14);
  font-size: var(--app-text-xs);
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.my-space-hero-chip:hover {
  background: rgba(255, 255, 255, 0.24);
}

/* BUSCADOR */
.my-space-search {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  padding: 14px 18px;
  border-radius: 12px;
  background: var(--app-color-surface);
  color: var(--app-color-text-muted);
  box-shadow: var(--app-shadow-md);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.my-space-search:hover {
  transform: translateY(-1px);
  box-shadow: var(--app-shadow-lg);
}
.my-space-search-icon {
  color: var(--app-color-primary);
  font-size: 18px;
}
.my-space-search-placeholder {
  flex: 1;
  font-size: var(--app-text-md);
}
.my-space-search-kbd {
  display: flex;
  gap: 3px;
}
.my-space-search-kbd kbd {
  padding: 2px 7px;
  border: 1px solid var(--app-color-border);
  border-radius: 5px;
  background: var(--app-color-surface-subtle);
  color: var(--app-color-text-muted);
  font-size: var(--app-text-xs);
}

/* TILES DE ACCESOS */
/* El contorno + fondo ya los pone .my-space-card que lo envuelve; acá solo
   queda de anclaje para las flechas de navegación (position:absolute). */
.my-space-tiles-frame {
  position: relative;
}
/* Un solo renglón siempre: nowrap + scroll horizontal propio, sin scrollbar
   nativa visible — la navegación es con las flechas (.my-space-tiles-nav),
   nunca arrastrando la barra. El padding vertical vive acá (no en el marco):
   "overflow-x: auto" fuerza a los navegadores a computar "overflow-y: auto"
   también (no se puede dejar en "visible" a propósito), así que el lift +
   sombra del hover de cada tile necesita este aire propio para no
   recortarse contra el borde del contenedor con scroll. */
.my-space-tiles {
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  padding: 16px 4px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}
.my-space-tiles::-webkit-scrollbar {
  display: none;
}
/* Wrapper de vuedraggable: "display: contents" lo saca del layout para que
   sus hijos (.my-space-tile) sigan siendo ítems directos del renglón flex
   de .my-space-tiles — así "Agregar" (fuera del draggable) queda en la
   misma fila sin un contenedor extra que rompa el flujo. */
.my-space-tiles-drag {
  display: contents;
}
/* Flotan sobre el borde de la primera/última tile en vez de reservar
   padding lateral propio en el marco (eso lo hacía demasiado ancho) — el
   círculo con sombra alcanza para separarse visualmente del contenido de
   abajo. */
.my-space-tiles-nav {
  position: absolute;
  top: 50%;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid var(--app-color-border);
  border-radius: 50%;
  background: var(--app-color-surface);
  color: var(--app-color-text-secondary);
  box-shadow: var(--app-shadow-md);
  cursor: pointer;
  transform: translateY(-50%);
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}
.my-space-tiles-nav:hover:not(:disabled) {
  border-color: var(--app-color-primary);
  background: var(--app-color-primary);
  color: var(--app-color-on-primary);
}
.my-space-tiles-nav:disabled {
  opacity: 0.35;
  cursor: default;
}
.my-space-tiles-nav--prev {
  left: 0;
}
.my-space-tiles-nav--next {
  right: 0;
}
/* Toda la tarjeta lleva el color: fondo tintado + ícono sólido, no solo un
   borde — primary para vistas (default), success para acciones
   (.my-space-tile--action). Se refuerza con .my-space-tile-type en texto
   (accesibilidad: docs/design-system/color-palette, "no comunicar estados
   únicamente con color"). */
.my-space-tile {
  position: relative;
  display: flex;
  flex: 0 0 108px;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 16px 8px 12px;
  border-radius: 12px;
  background: var(--app-color-primary-soft);
  border: 1px solid var(--app-color-primary-200);
  box-shadow: var(--app-shadow-sm);
  color: var(--app-color-text);
  cursor: grab;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
}
.my-space-tile:hover {
  transform: translateY(-2px);
  border-color: var(--app-color-primary);
  background: var(--app-color-primary-soft-hover);
  box-shadow: var(--app-shadow-md);
}
.my-space-tile--action {
  background: var(--app-color-success-soft);
  border-color: var(--app-color-success-border);
}
.my-space-tile--action:hover {
  border-color: var(--app-color-success);
}
.my-space-tile--dashboard {
  background: var(--app-color-warning-soft);
  border-color: var(--app-color-warning-border);
}
.my-space-tile--dashboard:hover {
  border-color: var(--app-color-warning);
}
.my-space-tile--ghost {
  opacity: 0.4;
}
.my-space-tile--dragging {
  cursor: grabbing;
  box-shadow: var(--app-shadow-lg);
}
.my-space-tile-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--app-color-surface);
  color: var(--app-color-text-muted);
  font-size: 12px;
  line-height: 1;
  opacity: 0;
  transition: opacity 0.15s ease, background-color 0.15s ease, color 0.15s ease;
}
.my-space-tile:hover .my-space-tile-remove {
  opacity: 1;
}
.my-space-tile-remove:hover {
  background: var(--app-color-danger);
  color: var(--app-color-white);
}
.my-space-tile-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 11px;
  background: var(--app-color-primary);
  color: var(--app-color-on-primary);
  font-size: 18px;
}
.my-space-tile--action .my-space-tile-icon {
  background: var(--app-color-success);
  color: var(--app-color-white);
}
.my-space-tile--dashboard .my-space-tile-icon {
  background: var(--app-color-warning);
  color: var(--app-color-white);
}
.my-space-tile-title {
  font-size: var(--app-text-xs);
  font-weight: var(--app-font-bold);
  color: var(--app-color-text);
  text-align: center;
  line-height: var(--app-leading-compact);
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}
.my-space-tile-type {
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--app-color-primary);
  color: var(--app-color-on-primary);
  font-size: 10px;
  font-weight: var(--app-font-bold);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.my-space-tile--action .my-space-tile-type {
  background: var(--app-color-success);
  color: var(--app-color-white);
}
.my-space-tile--dashboard .my-space-tile-type {
  background: var(--app-color-warning);
  color: var(--app-color-white);
}
.my-space-tile--add {
  cursor: pointer;
  border-style: dashed;
  border-color: var(--app-color-border-strong);
  background: var(--app-color-surface-subtle);
  box-shadow: none;
}
.my-space-tile--add .my-space-tile-icon {
  background: var(--app-color-surface-muted);
  color: var(--app-color-text-secondary);
}
.my-space-tile--add .my-space-tile-title {
  color: var(--app-color-text-secondary);
}
.my-space-tile--add:hover {
  border-color: var(--app-color-primary);
  background: var(--app-color-primary-soft);
}
.my-space-tile--add:hover .my-space-tile-icon {
  background: var(--app-color-primary);
  color: var(--app-color-on-primary);
}
.my-space-tiles-hint {
  margin-top: 10px;
  font-size: var(--app-text-xs);
}
.my-space-tiles-hint kbd {
  padding: 1px 5px;
  border: 1px solid var(--app-color-border);
  border-radius: 4px;
  background: var(--app-color-surface);
  /* Sin token menor a --app-text-xs (12px); se conserva el valor literal
     para no romper el tamaño compacto del hint. */
  font-size: 10px;
}

/* CARDS DEL DÍA */
/* height:100% + grid-template-rows:1fr: .my-space-main ya llega con alto
   definido (grid-item estirado por .my-space-layout) — esto lo hereda un
   nivel más adentro para que las 3 cards también se estiren, en vez de
   quedar con su alto mínimo y dejar aire abajo. */
.my-space-day-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: 1fr;
  height: 100%;
  gap: 14px;
}
/* Título, contenido y footer comparten un mismo contorno + fondo — mismo
   lenguaje en toda la sección, sin distinguir vacío/con datos acá: ese aviso
   ahora lo da <EmptyState> (docs/list-view-interfaces), con su propio
   borde punteado, así no se duplica la señal en dos niveles. */
.my-space-card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--app-color-border-strong);
  border-radius: 12px;
  background: var(--app-color-surface);
  box-shadow: var(--app-shadow-sm);
  overflow: hidden;
}
.my-space-card-header {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  background: linear-gradient(135deg, var(--app-color-primary-soft) 0%, var(--app-color-surface) 70%);
  border-bottom: 1px solid var(--app-color-border);
}
.my-space-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: var(--app-text-sm);
  font-weight: var(--app-font-bold);
  color: var(--app-color-text);
}
.my-space-card-refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-left: auto;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--app-color-text-muted);
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease;
}
.my-space-card-refresh:hover:not(:disabled) {
  background: var(--app-color-primary-soft-hover);
  color: var(--app-color-primary);
}
.my-space-card-refresh:disabled {
  cursor: default;
  opacity: .55;
}
.my-space-card-refresh__icon--spinning {
  animation: my-space-card-refresh-spin .8s linear infinite;
}
.my-space-card-collapse {
  display: none;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--app-color-text-muted);
}
.my-space-card-expand {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-left: 3px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--app-color-text-muted);
  cursor: pointer;
  outline: 0;
  transition: background-color .15s ease, color .15s ease;
}
.my-space-card-expand:hover,
.my-space-card-expand:focus,
.my-space-card-expand:active {
  border: 0 !important;
  background: var(--app-color-primary-soft-hover);
  box-shadow: none !important;
  color: var(--app-color-primary);
  outline: 0 !important;
}
.my-space-card--expanded {
  position: fixed;
  top: 24px;
  bottom: 24px;
  right: 24px;
  left: 24px;
  /* Queda por encima del shell (1030), pero debajo de los modales estándar
     de BootstrapVue (1040/1050) para que confirmaciones y formularios no
     queden atrapados detrás de la vista ampliada. */
  z-index: 1035;
  width: auto;
  max-width: 1040px;
  margin-right: auto;
  margin-left: auto;
  height: auto;
  max-height: none;
  overflow: visible;
  box-shadow: var(--app-shadow-md);
}
/* Los widgets scrollables tienen una altura compacta dentro del tablero.
   En la vista ampliada debe prevalecer el alto del diálogo para aprovechar
   la pantalla, no ese límite de 340px. */
.my-space-card.my-space-card--expanded.my-space-card--scrollable {
  height: calc(100vh - 48px) !important;
  height: calc(100dvh - 48px) !important;
  max-height: none !important;
}
.my-space-card-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 0;
  background: rgba(11, 31, 59, .48);
  cursor: default;
}
/* Las confirmaciones se montan al final del body. Se mantiene un margen por
   encima de la card ampliada para los diálogos invocados desde un widget. */
.my-space-confirm-modal {
  z-index: 1060 !important;
}
.my-space-confirm-header--success {
  border-bottom: 2px solid var(--app-color-success) !important;
  background: var(--app-color-success-soft) !important;
}
.my-space-confirm-header--success .app-confirm-header__title {
  color: var(--app-color-success) !important;
}
.my-space-completion-confirm-dialog {
  max-width: 370px;
}
.my-space-card--expanded > :not(.my-space-card-modal-backdrop) {
  position: relative;
  z-index: 1;
}
.my-space-card--expanded .my-space-card-body {
  overflow-y: auto;
  background: var(--app-color-surface);
}
.my-space-calendar-header-actions {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: auto;
}
.my-space-calendar-header-actions .my-space-card-refresh {
  margin-left: 0;
}
.my-space-calendar-configure,
.my-space-calendar-primary-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: 1px solid var(--app-color-primary-border);
  border-radius: 7px;
  background: var(--app-color-surface);
  color: var(--app-color-primary);
  font-size: var(--app-text-xs);
  font-weight: var(--app-font-bold);
  transition: background-color .15s ease, color .15s ease;
}
.my-space-calendar-configure {
  min-height: 28px;
  padding: 4px 8px;
}
.my-space-calendar-configure:hover,
.my-space-calendar-primary-action:hover {
  background: var(--app-color-primary);
  color: var(--app-color-on-primary);
}
@keyframes my-space-card-refresh-spin {
  to { transform: rotate(360deg); }
}
/* Ícono en placa sólida (mismo lenguaje que .my-space-tile-icon): ancla
   visual del header, no solo un glifo suelto junto al texto. */
.my-space-card-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: var(--app-color-primary);
  color: var(--app-color-on-primary);
  font-size: 14px;
  box-shadow: var(--app-shadow-sm);
}
/* Mismo lenguaje que .my-space-tiles-frame: contorno sutil + fondo apenas
   más claro. Sólido cuando hay contenido; punteado (--empty) cuando no hay
   nada para mostrar — la línea es la señal de "vacío", no un color aparte. */
.my-space-card-body {
  flex: 1;
  min-height: 120px;
  padding: 10px 14px;
}
.my-space-card--scrollable {
  /* La altura coincide con el acceso a tableros lateral. Si hay más de tres
     widgets, cada nueva fila conserva esta misma medida. */
  height: 340px;
  max-height: 340px;
}
.my-space-card--scrollable .my-space-card-body {
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
}
.my-space-card--reminders-top .my-space-card-body {
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
}
.my-space-card-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: var(--app-color-text-muted);
}
.my-space-calendar-day-label {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
  color: var(--app-color-text-secondary);
  font-size: var(--app-text-xs);
  text-transform: capitalize;
}
.my-space-calendar-day-label > span {
  padding: 2px 6px;
  border-radius: 999px;
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
  font-size: 10px;
  font-weight: var(--app-font-bold);
  letter-spacing: .4px;
}
.my-space-calendar-day-label small {
  color: var(--app-color-text-muted);
}
.my-space-calendar-event {
  display: grid;
  grid-template-columns: 55px 3px minmax(0, 1fr) auto;
  gap: 9px;
  align-items: stretch;
  padding: 9px 0;
  border-top: 1px solid var(--app-color-border);
}
.my-space-calendar-event--linked {
  margin: 0 -6px;
  padding: 9px 6px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color .15s ease;
}
.my-space-calendar-event--linked:hover,
.my-space-calendar-event--linked:focus {
  outline: 0;
  background: var(--app-color-primary-soft);
}
.my-space-calendar-event--active {
  margin-right: -6px;
  margin-left: -6px;
  padding-right: 6px;
  padding-left: 6px;
  border-radius: 8px;
  background: var(--app-color-primary-soft);
}
.my-space-calendar-event--active .my-space-calendar-event__line {
  background: var(--app-color-success);
  box-shadow: 0 0 0 2px var(--app-color-success-soft);
}
.my-space-calendar-event--finished {
  margin-right: -6px;
  margin-left: -6px;
  padding-right: 6px;
  padding-left: 6px;
  border-radius: 8px;
  background: var(--app-color-danger-soft);
}
.my-space-calendar-event--finished .my-space-calendar-event__line {
  background: var(--app-color-danger);
  box-shadow: 0 0 0 2px var(--app-color-danger-soft);
}
.my-space-calendar-event--finished .my-space-calendar-event__time {
  color: var(--app-color-danger);
}
.my-space-calendar-event--pending {
  opacity: .75;
}
.my-space-calendar-event--cancelled {
  opacity: .55;
  text-decoration: line-through;
}
.my-space-calendar-event__time {
  padding-top: 1px;
  color: var(--app-color-primary);
  font-size: var(--app-text-xs);
  font-weight: var(--app-font-bold);
  white-space: nowrap;
}
.my-space-calendar-event__time--previous-day {
  display: flex;
  flex-direction: column;
  gap: 1px;
  line-height: 1.15;
}
.my-space-calendar-event__time--previous-day small {
  font-size: 10px;
  font-weight: var(--app-font-regular);
}
.my-space-calendar-event__line {
  border-radius: 99px;
  background: var(--app-color-primary);
}
.my-space-calendar-event__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}
.my-space-calendar-event__heading {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.my-space-calendar-event__copy strong {
  overflow: hidden;
  color: var(--app-color-text);
  font-size: var(--app-text-sm);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.my-space-calendar-event__status {
  flex: 0 0 auto;
  padding: 3px 5px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: var(--app-font-bold);
}
.my-space-calendar-event__active {
  flex: 0 0 auto;
  padding: 3px 5px;
  border-radius: 4px;
  background: var(--app-color-success);
  color: var(--app-color-white);
  font-size: 10px;
  font-weight: var(--app-font-bold);
}
.my-space-calendar-event__finished {
  flex: 0 0 auto;
  padding: 3px 5px;
  border-radius: 4px;
  background: var(--app-color-danger);
  color: var(--app-color-white);
  font-size: 10px;
  font-weight: var(--app-font-bold);
}
.my-space-calendar-event__status--confirmed {
  background: var(--app-color-success-soft);
  color: var(--app-color-success);
}
.my-space-calendar-event__status--pending {
  background: var(--app-color-warning-soft);
  color: var(--app-color-warning);
}
.my-space-calendar-event__status--cancelled {
  background: var(--app-color-danger-soft);
  color: var(--app-color-danger);
}
.my-space-calendar-event__copy small {
  overflow: hidden;
  color: var(--app-color-text-muted);
  font-size: var(--app-text-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.my-space-calendar-event__description {
  display: -webkit-box;
  white-space: normal !important;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}
.my-space-calendar-event__recurrence {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 2px;
  color: var(--app-color-primary) !important;
}
.my-space-calendar-event__actions {
  display: inline-flex;
  align-items: center;
  align-self: center;
  gap: 4px;
}
.my-space-calendar-event__action,
.my-space-calendar-event__link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--app-color-primary);
}
.my-space-calendar-event__action:hover {
  background: var(--app-color-primary-soft-hover);
}
.my-space-calendar-welcome {
  display: flex;
  align-items: center;
  min-height: 180px;
  flex-direction: column;
  justify-content: center;
  padding: 18px 8px;
  border: 1px dashed var(--app-color-primary-200);
  border-radius: 10px;
  background: linear-gradient(160deg, var(--app-color-primary-soft) 0%, var(--app-color-surface-subtle) 100%);
  color: var(--app-color-text-secondary);
  text-align: center;
}
.my-space-calendar-welcome__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  margin-bottom: 10px;
  border-radius: 12px;
  background: var(--app-color-primary);
  color: var(--app-color-on-primary);
  box-shadow: var(--app-shadow-md);
}
.my-space-calendar-welcome p {
  max-width: 270px;
  margin: 7px 0 14px;
  font-size: var(--app-text-xs);
  line-height: 1.5;
}
.my-space-calendar-primary-action {
  padding: 8px 12px;
  background: var(--app-color-primary);
  color: var(--app-color-on-primary);
}
.my-space-calendar-modal-intro {
  color: var(--app-color-text-secondary);
  font-size: var(--app-text-sm);
  line-height: 1.5;
}
.my-space-calendar-unlink {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 8px;
  border: 1px solid var(--app-color-danger-border);
  border-radius: 6px;
  background: var(--app-color-danger-soft);
  color: var(--app-color-danger);
  font-size: var(--app-text-xs);
  font-weight: var(--app-font-bold);
}
.my-space-calendar-unlink:hover:not(:disabled) {
  background: var(--app-color-danger);
  color: var(--app-color-white);
}
/* EmptyState es global (86+ vistas) — no se toca su componente; acá se
   perfora el scope solo para los widgets de Mi Espacio, más expresivo que
   el bloque gris estándar de listados. */
.my-space-card-body ::v-deep .app-empty-state {
  gap: 10px;
  padding: 30px 16px;
  border-style: dashed;
  border-color: var(--app-color-primary-200);
  background: linear-gradient(160deg, var(--app-color-primary-soft) 0%, var(--app-color-surface-subtle) 100%);
}
.my-space-card-body ::v-deep .app-empty-state__icon {
  width: 52px;
  height: 52px;
  border: none;
  background: var(--app-color-primary);
  color: var(--app-color-on-primary);
  box-shadow: var(--app-shadow-md);
  animation: my-space-empty-float 3s ease-in-out infinite;
}
.my-space-card-body ::v-deep .app-empty-state__title {
  color: var(--app-color-text-secondary);
}
@keyframes my-space-empty-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
@media (prefers-reduced-motion: reduce) {
  .my-space-card-body ::v-deep .app-empty-state__icon {
    animation: none;
  }
}
.my-space-day-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  border-bottom: 1px solid var(--app-color-border);
  cursor: pointer;
  transition: background-color 0.1s ease;
}
.my-space-day-item:hover {
  background: var(--app-color-surface-subtle);
}
.my-space-day-item:last-child {
  border-bottom: none;
}
.my-space-day-item--task {
  gap: 10px;
  padding-top: 11px;
  padding-bottom: 11px;
  border-bottom-width: 2px;
}
.my-space-day-item-time {
  flex: 0 0 auto;
  font-size: var(--app-text-xs);
  font-weight: var(--app-font-bold);
  color: var(--app-color-primary);
  white-space: nowrap;
}
.my-space-day-item--overdue .my-space-day-item-time {
  color: var(--app-color-danger);
}
.my-space-day-item--overdue {
  box-shadow: inset 3px 0 0 0 var(--app-color-danger);
}
.my-space-next-reminder {
  margin-bottom: 8px;
  padding: 11px 12px;
  border: 1px solid var(--app-color-info-border);
  border-left: 4px solid var(--app-color-primary);
  border-radius: 8px;
  background: var(--app-color-primary-soft);
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}
.my-space-next-reminder:hover {
  border-color: var(--app-color-primary);
  background: var(--app-color-primary-soft-hover);
}
.my-space-next-reminder__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.my-space-next-reminder__label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--app-color-primary);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .04em;
  text-transform: uppercase;
}
.my-space-next-reminder__time {
  color: var(--app-color-primary-hover);
  font-size: var(--app-text-xs);
  font-weight: var(--app-font-bold);
  white-space: nowrap;
}
.my-space-next-reminder__actions {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}
.my-space-next-reminder__title {
  overflow: hidden;
  margin-top: 4px;
  color: var(--app-color-text);
  font-size: var(--app-text-sm);
  font-weight: var(--app-font-bold);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.my-space-day-item-text {
  flex: 1;
  min-width: 0;
  font-size: var(--app-text-sm);
  color: var(--app-color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.my-space-day-item-text strong {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.my-space-item-complete {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid var(--app-color-success-border);
  border-radius: 50%;
  background: var(--app-color-success-soft);
  color: var(--app-color-success-hover);
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease, transform .15s ease;
}
.my-space-item-complete:hover:not(:disabled) {
  border-color: var(--app-color-success);
  background: var(--app-color-success);
  color: var(--app-color-on-primary);
  transform: scale(1.06);
}
.my-space-item-complete:disabled {
  cursor: default;
  opacity: .55;
}
.my-space-task-category {
  display: inline-flex;
  flex: 0 0 auto;
  max-width: 42%;
  overflow: hidden;
  padding: 3px 7px;
  border: 1px solid var(--app-color-info-border);
  border-radius: 999px;
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary-hover);
  font-size: 10px;
  font-weight: 700;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.my-space-task-category--empty {
  border-color: var(--app-color-border);
  background: var(--app-color-surface-subtle);
  color: var(--app-color-text-muted);
}
.my-space-task-tree {
  border-bottom: 2px solid var(--app-color-border);
}
.my-space-task-tree:last-child {
  border-bottom: none;
}
.my-space-task-tree__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  color: var(--app-color-text-secondary);
  cursor: pointer;
  list-style: none;
  transition: background-color .15s ease, color .15s ease;
}
.my-space-task-tree__summary::-webkit-details-marker {
  display: none;
}
.my-space-task-tree__summary:hover {
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary-hover);
}
.my-space-task-tree__group {
  display: inline-flex;
  flex: 1;
  align-items: center;
  gap: 7px;
  min-width: 0;
  overflow: hidden;
  font-size: var(--app-text-sm);
  font-weight: var(--app-font-bold);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.my-space-task-tree__group-copy {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}
.my-space-task-tree__group-name,
.my-space-task-tree__customer-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.my-space-task-tree__customer-name {
  margin-top: 1px;
  color: var(--app-color-text-muted);
  font-size: 10px;
  font-weight: 600;
}
.my-space-task-tree__meta {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}
.my-space-task-tree__count {
  min-width: 22px;
  padding: 3px 6px;
  border-radius: 999px;
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary-hover);
  font-size: 10px;
  font-weight: 800;
}
.my-space-task-tree__chevron {
  color: var(--app-color-text-muted);
  transition: transform .15s ease;
}
.my-space-task-tree[open] .my-space-task-tree__summary {
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary-hover);
}
.my-space-task-tree[open] .my-space-task-tree__chevron {
  transform: rotate(180deg);
}
.my-space-task-tree__children {
  border-top: 1px solid var(--app-color-primary-200);
  background: var(--app-color-surface-subtle);
}
.my-space-task-tree__task {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  padding: 8px 10px 8px 22px;
  border-bottom: 1px solid var(--app-color-border);
  cursor: pointer;
  transition: background-color .15s ease;
}
.my-space-task-tree__task:last-child {
  border-bottom: none;
}
.my-space-task-tree__task:hover {
  background: var(--app-color-primary-soft);
}
.my-space-task-tree__task-icon {
  flex: 0 0 auto;
  color: var(--app-color-text-muted);
  font-size: 14px;
}
.my-space-day-item-preview {
  display: block;
  font-size: var(--app-text-xs);
  color: var(--app-color-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.my-space-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 42px;
  padding: 9px 14px;
  background: linear-gradient(90deg, var(--app-color-primary-soft) 0%, var(--app-color-surface) 100%);
  border-top: 1px solid var(--app-color-primary-200);
  box-shadow: inset 3px 0 0 var(--app-color-primary);
  font-size: var(--app-text-sm);
  font-weight: var(--app-font-bold);
  letter-spacing: 0.1px;
  color: var(--app-color-primary-hover);
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}
.my-space-card-footer:hover {
  background: var(--app-color-primary-soft-hover);
  border-top-color: var(--app-color-primary);
  box-shadow: inset 4px 0 0 var(--app-color-primary-hover);
  color: var(--app-color-primary-active);
}
.my-space-card-footer-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--app-color-primary);
  color: var(--app-color-on-primary);
  transition: transform 0.15s ease, background-color 0.15s ease;
}
.my-space-card-footer:hover .my-space-card-footer-arrow {
  transform: translateX(3px);
  background: var(--app-color-primary-hover);
}

@media (max-width: 991.98px) {
  /* El acceso a Tableros pasa a ocupar el ancho completo, debajo de todo lo
     demás — el sidebar de altura completa solo tiene sentido con las dos
     columnas lado a lado. Mismo criterio para Recordatorios/Calendario: se
     apilan en una sola columna, en orden de aparición en el DOM. */
  .my-space-layout {
    grid-template-columns: 1fr;
  }
  .my-space-top-grid {
    grid-template-columns: 1fr;
  }
  .my-space-card--accesos,
  .my-space-card--reminders-top,
  .my-space-card--calendar {
    grid-column: 1;
    grid-row: auto;
  }
}
@media (max-width: 767.98px) {
  .my-space-card--expanded {
    top: 8px;
    right: 8px;
    bottom: 8px;
    left: 8px;
    width: auto;
  }
  .my-space-card.my-space-card--expanded.my-space-card--scrollable {
    height: calc(100vh - 16px) !important;
    height: calc(100dvh - 16px) !important;
  }
  /* En mobile el orden prioriza lo accionable: buscador, accesos, agenda y
     tareas. El QR queda al final, después de todas las cards operativas. */
  .my-space-top-grid > .my-space-hero {
    grid-column: 1;
    grid-row: 1;
  }
  .my-space-card--accesos {
    grid-column: 1;
    grid-row: 2;
  }
  .my-space-card--reminders-top {
    grid-column: 1;
    grid-row: 3;
  }
  .my-space-card--calendar {
    grid-column: 1;
    grid-row: 4;
  }
  /* Solo en mobile: el usuario decide qué cards operativas mantener
     abiertas. En desktop no se aplica ni se muestra el control. */
  .my-space-card-collapse {
    display: inline-flex;
  }
  .my-space-card-collapse:hover,
  .my-space-card-collapse:focus {
    background: var(--app-color-primary-soft-hover);
    color: var(--app-color-primary);
  }
  .my-space-card--mobile-collapsed .my-space-card-body,
  .my-space-card--mobile-collapsed .my-space-card-footer {
    display: none;
  }
  .my-space-card--mobile-collapsed.my-space-card--scrollable {
    height: auto;
    min-height: 0;
  }
  .my-space-card--mobile-collapsed .my-space-card-header {
    border-bottom: 0;
  }
  .my-space-day-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 575.98px) {
  .my-space-hero {
    padding: 20px 16px 24px;
    border-radius: 12px;
  }
  .my-space-hero-company {
    right: 16px;
    margin: 0;
  }
  .my-space-hero-company--full {
    display: none;
  }
  .my-space-hero-company--compact {
    display: inline-flex;
  }
  .my-space-hero-content {
    padding-top: 0;
  }
  .my-space-hero-greeting {
    font-size: var(--app-text-lg);
  }
  .my-space-search {
    padding: 12px 14px;
  }
  .my-space-search-kbd {
    display: none;
  }
  .my-space-tiles {
    gap: 10px;
    padding: 14px 4px;
  }
  .my-space-tile {
    flex-basis: 92px;
  }
  .my-space-tiles-nav {
    width: 24px;
    height: 24px;
  }
  /* Sin hover en touch: el botón de quitar queda siempre visible. */
  .my-space-tile-remove {
    opacity: 1;
  }
}
</style>
